/*
 * Copyright 2021, Offchain Labs, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *    http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/* eslint-env node */
'use strict';
Object.defineProperty(exports, "__esModule", { value: true });
exports.resetNetworksToDefault = exports.addDefaultLocalNetwork = exports.addCustomNetwork = exports.getEthBridgeInformation = exports.getL2Network = exports.getL1Network = exports.getNetwork = exports.l2Networks = exports.l1Networks = exports.getParentForNetwork = exports.isL1Network = exports.networks = void 0;
const signerOrProvider_1 = require("./signerOrProvider");
const errors_1 = require("../dataEntities/errors");
const constants_1 = require("./constants");
const RollupAdminLogic__factory_1 = require("../abi/factories/RollupAdminLogic__factory");
const mainnetTokenBridge = {
    l1GatewayRouter: '0x72Ce9c846789fdB6fC1f34aC4AD25Dd9ef7031ef',
    l2GatewayRouter: '0x5288c571Fd7aD117beA99bF60FE0846C4E84F933',
    l1ERC20Gateway: '0xa3A7B6F88361F48403514059F1F16C8E78d60EeC',
    l2ERC20Gateway: '0x09e9222E96E7B4AE2a407B98d48e330053351EEe',
    l1CustomGateway: '0xcEe284F754E854890e311e3280b767F80797180d',
    l2CustomGateway: '0x096760F208390250649E3e8763348E783AEF5562',
    l1WethGateway: '0xd92023E9d9911199a6711321D1277285e6d4e2db',
    l2WethGateway: '0x6c411aD3E74De3E7Bd422b94A27770f5B86C623B',
    l2Weth: '0x82aF49447D8a07e3bd95BD0d56f35241523fBab1',
    l1Weth: '0xc02aaa39b223fe8d0a0e5c4f27ead9083c756cc2',
    l1ProxyAdmin: '0x9aD46fac0Cf7f790E5be05A0F15223935A0c0aDa',
    l2ProxyAdmin: '0xd570aCE65C43af47101fC6250FD6fC63D1c22a86',
    l1MultiCall: '0x5ba1e12693dc8f9c48aad8770482f4739beed696',
    l2Multicall: '0x842eC2c7D803033Edf55E478F461FC547Bc54EB2',
};
const mainnetETHBridge = {
    bridge: '0x8315177aB297bA92A06054cE80a67Ed4DBd7ed3a',
    inbox: '0x4Dbd4fc535Ac27206064B68FfCf827b0A60BAB3f',
    sequencerInbox: '0x1c479675ad559DC151F6Ec7ed3FbF8ceE79582B6',
    outbox: '0x0B9857ae2D4A3DBe74ffE1d7DF045bb7F96E4840',
    rollup: '0x5eF0D09d1E6204141B4d37530808eD19f60FBa35',
    classicOutboxes: {
        '0x667e23ABd27E623c11d4CC00ca3EC4d0bD63337a': 0,
        '0x760723CD2e632826c38Fef8CD438A4CC7E7E1A40': 30,
    },
};
/**
 * Storage for all networks, either L1, L2 or L3.
 */
exports.networks = {
    1: {
        chainID: 1,
        name: 'Mainnet',
        explorerUrl: 'https://etherscan.io',
        partnerChainIDs: [42161, 42170],
        blockTime: 14,
        isCustom: false,
        isArbitrum: false,
    },
    1338: {
        chainID: 1338,
        name: 'Hardhat_Mainnet_Fork',
        explorerUrl: 'https://etherscan.io',
        partnerChainIDs: [42161],
        blockTime: 1,
        isCustom: false,
        isArbitrum: false,
    },
    11155111: {
        chainID: 11155111,
        name: 'Sepolia',
        explorerUrl: 'https://sepolia.etherscan.io',
        partnerChainIDs: [421614],
        blockTime: 12,
        isCustom: false,
        isArbitrum: false,
    },
    17000: {
        chainID: 17000,
        name: 'Holesky',
        explorerUrl: 'https://holesky.etherscan.io',
        partnerChainIDs: [],
        blockTime: 12,
        isCustom: false,
        isArbitrum: false,
    },
    42161: {
        chainID: 42161,
        name: 'Arbitrum One',
        explorerUrl: 'https://arbiscan.io',
        partnerChainID: 1,
        partnerChainIDs: [],
        isArbitrum: true,
        tokenBridge: mainnetTokenBridge,
        ethBridge: mainnetETHBridge,
        teleporter: {
            l1Teleporter: '0xCBd9c6e310D6AaDeF9F025f716284162F0158992',
            l2ForwarderFactory: '0x791d2AbC6c3A459E13B9AdF54Fb5e97B7Af38f87',
        },
        confirmPeriodBlocks: 45818,
        isCustom: false,
        retryableLifetimeSeconds: constants_1.SEVEN_DAYS_IN_SECONDS,
        nitroGenesisBlock: 22207817,
        nitroGenesisL1Block: 15447158,
        /**
         * Finalisation on mainnet can be up to 2 epochs = 64 blocks on mainnet
         * We add 10 minutes for the system to create and redeem the ticket, plus some extra buffer of time
         * (Total timeout: 30 minutes)
         */
        depositTimeout: 1800000,
        blockTime: constants_1.ARB_MINIMUM_BLOCK_TIME_IN_SECONDS,
    },
    42170: {
        chainID: 42170,
        confirmPeriodBlocks: 45818,
        ethBridge: {
            bridge: '0xC1Ebd02f738644983b6C4B2d440b8e77DdE276Bd',
            inbox: '0xc4448b71118c9071Bcb9734A0EAc55D18A153949',
            outbox: '0xD4B80C3D7240325D18E645B49e6535A3Bf95cc58',
            rollup: '0xFb209827c58283535b744575e11953DCC4bEAD88',
            sequencerInbox: '0x211E1c4c7f1bF5351Ac850Ed10FD68CFfCF6c21b',
        },
        explorerUrl: 'https://nova.arbiscan.io',
        isArbitrum: true,
        isCustom: false,
        name: 'Arbitrum Nova',
        partnerChainID: 1,
        partnerChainIDs: [],
        retryableLifetimeSeconds: constants_1.SEVEN_DAYS_IN_SECONDS,
        tokenBridge: {
            l1CustomGateway: '0x23122da8C581AA7E0d07A36Ff1f16F799650232f',
            l1ERC20Gateway: '0xB2535b988dcE19f9D71dfB22dB6da744aCac21bf',
            l1GatewayRouter: '0xC840838Bc438d73C16c2f8b22D2Ce3669963cD48',
            l1MultiCall: '0x8896D23AfEA159a5e9b72C9Eb3DC4E2684A38EA3',
            l1ProxyAdmin: '0xa8f7DdEd54a726eB873E98bFF2C95ABF2d03e560',
            l1Weth: '0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2',
            l1WethGateway: '0xE4E2121b479017955Be0b175305B35f312330BaE',
            l2CustomGateway: '0xbf544970E6BD77b21C6492C281AB60d0770451F4',
            l2ERC20Gateway: '0xcF9bAb7e53DDe48A6DC4f286CB14e05298799257',
            l2GatewayRouter: '0x21903d3F8176b1a0c17E953Cd896610Be9fFDFa8',
            l2Multicall: '0x5e1eE626420A354BbC9a95FeA1BAd4492e3bcB86',
            l2ProxyAdmin: '0xada790b026097BfB36a5ed696859b97a96CEd92C',
            l2Weth: '0x722E8BdD2ce80A4422E880164f2079488e115365',
            l2WethGateway: '0x7626841cB6113412F9c88D3ADC720C9FAC88D9eD',
        },
        teleporter: {
            l1Teleporter: '0xCBd9c6e310D6AaDeF9F025f716284162F0158992',
            l2ForwarderFactory: '0x791d2AbC6c3A459E13B9AdF54Fb5e97B7Af38f87',
        },
        nitroGenesisBlock: 0,
        nitroGenesisL1Block: 0,
        /**
         * Finalisation on mainnet can be up to 2 epochs = 64 blocks on mainnet
         * We add 10 minutes for the system to create and redeem the ticket, plus some extra buffer of time
         * (Total timeout: 30 minutes)
         */
        depositTimeout: 1800000,
        blockTime: constants_1.ARB_MINIMUM_BLOCK_TIME_IN_SECONDS,
    },
    421614: {
        chainID: 421614,
        confirmPeriodBlocks: 20,
        ethBridge: {
            bridge: '0x38f918D0E9F1b721EDaA41302E399fa1B79333a9',
            inbox: '0xaAe29B0366299461418F5324a79Afc425BE5ae21',
            outbox: '0x65f07C7D521164a4d5DaC6eB8Fac8DA067A3B78F',
            rollup: '0xd80810638dbDF9081b72C1B33c65375e807281C8',
            sequencerInbox: '0x6c97864CE4bEf387dE0b3310A44230f7E3F1be0D',
        },
        explorerUrl: 'https://sepolia-explorer.arbitrum.io',
        isArbitrum: true,
        isCustom: false,
        name: 'Arbitrum Rollup Sepolia Testnet',
        partnerChainID: 11155111,
        partnerChainIDs: [],
        retryableLifetimeSeconds: constants_1.SEVEN_DAYS_IN_SECONDS,
        tokenBridge: {
            l1CustomGateway: '0xba2F7B6eAe1F9d174199C5E4867b563E0eaC40F3',
            l1ERC20Gateway: '0x902b3E5f8F19571859F4AB1003B960a5dF693aFF',
            l1GatewayRouter: '0xcE18836b233C83325Cc8848CA4487e94C6288264',
            l1MultiCall: '0xded9AD2E65F3c4315745dD915Dbe0A4Df61b2320',
            l1ProxyAdmin: '0xDBFC2FfB44A5D841aB42b0882711ed6e5A9244b0',
            l1Weth: '0x7b79995e5f793A07Bc00c21412e50Ecae098E7f9',
            l1WethGateway: '0xA8aD8d7e13cbf556eE75CB0324c13535d8100e1E',
            l2CustomGateway: '0x8Ca1e1AC0f260BC4dA7Dd60aCA6CA66208E642C5',
            l2ERC20Gateway: '0x6e244cD02BBB8a6dbd7F626f05B2ef82151Ab502',
            l2GatewayRouter: '0x9fDD1C4E4AA24EEc1d913FABea925594a20d43C7',
            l2Multicall: '0xA115146782b7143fAdB3065D86eACB54c169d092',
            l2ProxyAdmin: '0x715D99480b77A8d9D603638e593a539E21345FdF',
            l2Weth: '0x980B62Da83eFf3D4576C647993b0c1D7faf17c73',
            l2WethGateway: '0xCFB1f08A4852699a979909e22c30263ca249556D',
        },
        teleporter: {
            l1Teleporter: '0x9E86BbF020594D7FFe05bF32EEDE5b973579A968',
            l2ForwarderFactory: '0x88feBaFBb4E36A4E7E8874E4c9Fd73A9D59C2E7c',
        },
        nitroGenesisBlock: 0,
        nitroGenesisL1Block: 0,
        depositTimeout: 1800000,
        blockTime: constants_1.ARB_MINIMUM_BLOCK_TIME_IN_SECONDS,
    },
};
/**
 * Determines if a chain is a parent of *any* other chain. Could be an L1 or an L2 chain.
 */
const isParentChain = (chain) => {
    return chain.partnerChainIDs.length > 0;
};
/**
 * Determines if a chain is an Arbitrum chain. Could be an L2 or an L3 chain.
 */
const isArbitrumNetwork = (chain) => {
    return chain.isArbitrum;
};
/**
 * Determines if a chain is specifically an L1 chain (not L2 or L3).
 */
const isL1Network = (chain) => {
    return !chain.isArbitrum;
};
exports.isL1Network = isL1Network;
/**
 * Builds an object that is a list of chains filtered by the provided predicate function indexed by their chain id
 * @param filterFn - A predicate function to determine if a chain should be included.
 * @return An object with only the filtered chains.
 */
const getChainsByType = (filterFn) => {
    return Object.entries(exports.networks).reduce((accumulator, [chainId, chainData]) => {
        if (filterFn(chainData)) {
            accumulator[chainId] = chainData;
        }
        return accumulator;
    }, {});
};
const getL1Chains = () => getChainsByType(exports.isL1Network);
const getArbitrumChains = () => getChainsByType(isArbitrumNetwork);
/**
 * Returns the parent chain for the given chain.
 */
const getParentForNetwork = (chain) => {
    if (!isArbitrumNetwork(chain)) {
        throw new errors_1.ArbSdkError(`Chain ${chain.chainID} is not an Arbitrum chain.`);
    }
    const parentChain = exports.networks[chain.partnerChainID];
    if (!parentChain || !isParentChain(parentChain)) {
        throw new errors_1.ArbSdkError(`Parent chain ${chain.partnerChainID} not recognized for chain ${chain.chainID}.`);
    }
    return parentChain;
};
exports.getParentForNetwork = getParentForNetwork;
/**
 * Returns a list of children chains for the given chain.
 */
const getChildrenForNetwork = (chain) => {
    const arbitrumChains = getArbitrumChains();
    return Object.values(arbitrumChains).filter(arbitrumChain => arbitrumChain.partnerChainID === chain.chainID);
};
/**
 * Index of *only* L1 chains that have been added.
 */
exports.l1Networks = getL1Chains();
/**
 * Index of all Arbitrum chains that have been added.
 */
exports.l2Networks = getArbitrumChains();
/**
 * Returns the network associated with the given Signer, Provider or chain id.
 * @note Throws if the chain is not recognized.
 */
const getNetwork = async (signerOrProviderOrChainID, layer) => {
    const chainID = await (async () => {
        if (typeof signerOrProviderOrChainID === 'number') {
            return signerOrProviderOrChainID;
        }
        const provider = signerOrProvider_1.SignerProviderUtils.getProviderOrThrow(signerOrProviderOrChainID);
        const { chainId } = await provider.getNetwork();
        return chainId;
    })();
    let network = undefined;
    if (layer === 1) {
        network = getL1Chains()[chainID];
    }
    else {
        network = getArbitrumChains()[chainID];
    }
    if (!network) {
        throw new errors_1.ArbSdkError(`Unrecognized network ${chainID}.`);
    }
    return network;
};
exports.getNetwork = getNetwork;
/**
 * Returns the L1 chain associated with the given signer, provider or chain id.
 *
 * @note Throws if the chain is not an L1 chain.
 */
const getL1Network = (signerOrProviderOrChainID) => {
    return (0, exports.getNetwork)(signerOrProviderOrChainID, 1);
};
exports.getL1Network = getL1Network;
/**
 * Returns the Arbitrum chain associated with the given signer, provider or chain id.
 *
 * @note Throws if the chain is not an Arbitrum chain.
 */
const getL2Network = (signerOrProviderOrChainID) => {
    return (0, exports.getNetwork)(signerOrProviderOrChainID, 2);
};
exports.getL2Network = getL2Network;
/**
 * Returns the addresses of all contracts that make up the ETH bridge
 * @param rollupContractAddress Address of the Rollup contract
 * @param l1SignerOrProvider A parent chain signer or provider
 * @returns EthBridge object with all information about the ETH bridge
 */
const getEthBridgeInformation = async (rollupContractAddress, l1SignerOrProvider) => {
    const rollup = RollupAdminLogic__factory_1.RollupAdminLogic__factory.connect(rollupContractAddress, l1SignerOrProvider);
    const [bridge, inbox, sequencerInbox, outbox] = await Promise.all([
        rollup.bridge(),
        rollup.inbox(),
        rollup.sequencerInbox(),
        rollup.outbox(),
    ]);
    return {
        bridge,
        inbox,
        sequencerInbox,
        outbox,
        rollup: rollupContractAddress,
    };
};
exports.getEthBridgeInformation = getEthBridgeInformation;
/**
 * Adds any chain to the global index of networks and updates the parent/child relationships.
 */
const addNetwork = (network) => {
    // store the network with the rest of the networks
    exports.networks[network.chainID] = network;
    // if it's a parent chain (L1 or L2), assign it as parent to all the children
    if (isParentChain(network)) {
        const children = getChildrenForNetwork(network);
        children.forEach(child => {
            child.partnerChainID = network.chainID;
        });
    }
    // if it's an arbitrum chain, add it to the parent's list of children
    if (isArbitrumNetwork(network)) {
        const parent = exports.networks[network.partnerChainID];
        if (!parent) {
            throw new errors_1.ArbSdkError(`Network ${network.chainID}'s parent network ${network.partnerChainID} is not recognized`);
        }
        parent.partnerChainIDs = [...parent.partnerChainIDs, network.chainID];
    }
    exports.l1Networks = getL1Chains();
    exports.l2Networks = getArbitrumChains();
};
/**
 * Registers a pair of custom L1 and L2 chains, or a single custom Arbitrum chain (L2 or L3).
 *
 * @param customL1Network the custom L1 chain (optional)
 * @param customL2Network the custom L2 or L3 chain
 */
const addCustomNetwork = ({ customL1Network, customL2Network, }) => {
    if (customL1Network) {
        if (customL1Network.chainID !== customL2Network.partnerChainID) {
            throw new errors_1.ArbSdkError(`Partner chain id for L2 network ${customL2Network.chainID} doesn't match the provided L1 network. Expected ${customL1Network.chainID} but got ${customL2Network.partnerChainID}.`);
        }
        // check the if the parent chain is in any of the lists
        if (exports.l1Networks[customL1Network.chainID]) {
            throw new errors_1.ArbSdkError(`Network ${customL1Network.chainID} already included`);
        }
        else if (!customL1Network.isCustom) {
            throw new errors_1.ArbSdkError(`Custom network ${customL1Network.chainID} must have isCustom flag set to true`);
        }
        addNetwork(customL1Network);
    }
    if (exports.l2Networks[customL2Network.chainID]) {
        throw new errors_1.ArbSdkError(`Network ${customL2Network.chainID} already included`);
    }
    else if (!customL2Network.isCustom) {
        throw new errors_1.ArbSdkError(`Custom network ${customL2Network.chainID} must have isCustom flag set to true`);
    }
    addNetwork(customL2Network);
};
exports.addCustomNetwork = addCustomNetwork;
/**
 * Registers a custom network that matches the one created by a Nitro local node. Useful in development.
 *
 * @see {@link https://github.com/OffchainLabs/nitro}
 */
const addDefaultLocalNetwork = () => {
    const defaultLocalL1Network = {
        blockTime: 10,
        chainID: 1337,
        explorerUrl: '',
        isCustom: true,
        name: 'EthLocal',
        partnerChainIDs: [412346],
        isArbitrum: false,
    };
    const defaultLocalL2Network = {
        chainID: 412346,
        confirmPeriodBlocks: 20,
        ethBridge: {
            bridge: '0x2b360A9881F21c3d7aa0Ea6cA0De2a3341d4eF3C',
            inbox: '0xfF4a24b22F94979E9ba5f3eb35838AA814bAD6F1',
            outbox: '0x49940929c7cA9b50Ff57a01d3a92817A414E6B9B',
            rollup: '0x65a59D67Da8e710Ef9A01eCa37f83f84AEdeC416',
            sequencerInbox: '0xE7362D0787b51d8C72D504803E5B1d6DcdA89540',
        },
        explorerUrl: '',
        isArbitrum: true,
        isCustom: true,
        name: 'ArbLocal',
        partnerChainID: 1337,
        partnerChainIDs: [],
        retryableLifetimeSeconds: 604800,
        nitroGenesisBlock: 0,
        nitroGenesisL1Block: 0,
        depositTimeout: 900000,
        tokenBridge: {
            l1CustomGateway: '0x3DF948c956e14175f43670407d5796b95Bb219D8',
            l1ERC20Gateway: '0x4A2bA922052bA54e29c5417bC979Daaf7D5Fe4f4',
            l1GatewayRouter: '0x525c2aBA45F66987217323E8a05EA400C65D06DC',
            l1MultiCall: '0xDB2D15a3EB70C347E0D2C2c7861cAFb946baAb48',
            l1ProxyAdmin: '0xe1080224B632A93951A7CFA33EeEa9Fd81558b5e',
            l1Weth: '0x408Da76E87511429485C32E4Ad647DD14823Fdc4',
            l1WethGateway: '0xF5FfD11A55AFD39377411Ab9856474D2a7Cb697e',
            l2CustomGateway: '0x525c2aBA45F66987217323E8a05EA400C65D06DC',
            l2ERC20Gateway: '0xe1080224B632A93951A7CFA33EeEa9Fd81558b5e',
            l2GatewayRouter: '0x1294b86822ff4976BfE136cB06CF43eC7FCF2574',
            l2Multicall: '0xDB2D15a3EB70C347E0D2C2c7861cAFb946baAb48',
            l2ProxyAdmin: '0xda52b25ddB0e3B9CC393b0690Ac62245Ac772527',
            l2Weth: '0x408Da76E87511429485C32E4Ad647DD14823Fdc4',
            l2WethGateway: '0x4A2bA922052bA54e29c5417bC979Daaf7D5Fe4f4',
        },
        blockTime: constants_1.ARB_MINIMUM_BLOCK_TIME_IN_SECONDS,
    };
    (0, exports.addCustomNetwork)({
        customL1Network: defaultLocalL1Network,
        customL2Network: defaultLocalL2Network,
    });
    return {
        l1Network: defaultLocalL1Network,
        l2Network: defaultLocalL2Network,
    };
};
exports.addDefaultLocalNetwork = addDefaultLocalNetwork;
/**
 * Creates a function that resets the networks index to default. Useful in development.
 */
const createNetworkStateHandler = () => {
    const initialState = JSON.parse(JSON.stringify(exports.networks));
    return {
        resetNetworksToDefault: () => {
            Object.keys(exports.networks).forEach(key => delete exports.networks[key]);
            Object.assign(exports.networks, JSON.parse(JSON.stringify(initialState)));
            exports.l1Networks = getL1Chains();
            exports.l2Networks = getArbitrumChains();
        },
    };
};
const { resetNetworksToDefault } = createNetworkStateHandler();
exports.resetNetworksToDefault = resetNetworksToDefault;
